// src/common/hooks/useLeadFeature.ts

import { useState, useCallback, useEffect } from 'react'
import { LeadFormData, ValidationResultInterface } from '../../../../common/types/interfaces'
import { useValidation } from '../../../../common/hooks/useValidation'
import { leadValidations } from '../../../../common/utils/constants'
import { useInput } from '../../../../common/hooks/useInput'
import { delayAction } from '../../../../common/utils/functions/delayAction'
import { LeadFormStage } from '../../../../common/types/enums'
import { getApi } from '../../../../api/api'

interface UseLeadFeatureProps {
  onSuccess?: () => void
}

export const useLeadFeature = ({ onSuccess }: UseLeadFeatureProps = {}) => {
  const [data, setData] = useState<LeadFormData>({
    phone: '',
    consent: false,
  })
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [disabled, setDisabled] = useState(true)
  const [stage, setStage] = useState<LeadFormStage>(LeadFormStage.FILL)
  const [message, setMessage] = useState('')
  const [api] = useState(getApi())

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    leadValidations,
  )

  const { inputHandler } = useInput(setData, checkAndAddValidation)

  const getValidationResult = useCallback(
    (fieldName: string) => {
      return validationResult.find((el) => el.name === fieldName)
    },
    [validationResult],
  )

  useEffect(() => {
    leadValidations.forEach((rule) => {
      checkAndAddValidation(rule.fieldName)
    })
  }, [checkAndAddValidation])

  useEffect(() => {
    for (const item in data) {
      if (leadValidations.map((el) => el.fieldName).includes(item)) {
        validate(item, data[item as keyof LeadFormData])
      }
    }
  }, [data])

  useEffect(() => {
    const allErrors: string[] = []

    validationResult
      .filter((el) => el.notValid)
      .forEach((el) => {
        allErrors.push(el.name)
      })

    if (!data.consent) {
      allErrors.push('consent')
    }

    const hasErrors = allErrors.length > 0
    setDisabled(hasErrors)
  }, [validationResult, data.consent])

  const toggleConsent = useCallback(() => {
    setData((prev) => ({ ...prev, consent: !prev.consent }))
    setDirty('consent')
  }, [setDirty])

  const resetForm = useCallback(() => {
    setData({ phone: '', consent: false })
    setStage(LeadFormStage.FILL)
    setMessage('')
  }, [])

  const formAction = useCallback(async () => {
    setStage(LeadFormStage.PROGRESS)
    setMessage('Отправка...')

    try {
      const country = process.env.REACT_APP_COUNTRY
      const suffix = country && country !== 'ru' ? ` ${country.toUpperCase()}` : ''
      const { phone } = data
      await api.sendLead({ phone: `${phone}${suffix}` })
      setStage(LeadFormStage.SUCCESS)
      setMessage('Спасибо! Мы свяжемся с вами')
      onSuccess?.()
      await delayAction(3000)
      resetForm()
    } catch (err) {
      setStage(LeadFormStage.ERROR)
      setMessage('Ошибка отправки. Попробуйте позже')
      await delayAction(3000)
      resetForm()
    }
  }, [data, api, onSuccess, resetForm])

  return {
    data,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    stage,
    message,
    toggleConsent,
    formAction,
  }
}
